import { ICol } from "../../../../interfaces";
import { getColSpan } from "../../Row/hiddenCellBorders";

/**
 * Calculate the maximum depth of a header structure.
 *
 * @param headers - An array of header objects defining the table structure.
 * @returns The maximum depth of the header structure.
 */
export const calculateHeaderDepth = (headers: ICol[]): number => {
  let maxDepth = 1;

  for (const header of headers) {
    if (header.cols && header.cols.length > 0) {
      const subDepth = 1 + calculateHeaderDepth(header.cols);
      maxDepth = Math.max(maxDepth, subDepth);
    }
  }

  return maxDepth;
};

/**
 * Count the total leaf columns in a nested structure.
 * Space cells count as 1 column width.
 *
 * @param headers - An array of header objects defining the table structure.
 * @returns The total number of leaf columns in the header structure.
 */
export const countLeafColumns = (headers: ICol[]): number => {
  let count = 0;

  for (const header of headers) {
    if (header.type === "space") {
      count += 1; // Space always counts as 1 column
    } else if (header.cols && header.cols.length > 0) {
      count += countLeafColumns(header.cols);
    } else {
      count += 1;
    }
  }

  return count;
};

export interface ICollapsedColumnRange {
  start: number;
  end: number;
}

/**
 * Flat leaf-column ranges [start, end) currently collapsed via the header
 * column-collapse feature, derived from `headers` alone so row rendering
 * never needs collapsed state stored on individual cells.
 *
 * @param headers - An array of header objects defining the table structure.
 * @returns Flat leaf-column ranges currently collapsed.
 */
export const getCollapsedColumnRanges = (
  headers: ICol[],
): ICollapsedColumnRange[] => {
  const ranges: ICollapsedColumnRange[] = [];
  let pos = 0;

  for (const header of headers) {
    const span = countLeafColumns([header]);
    if (header.type === "split" && header.collapsed) {
      ranges.push({ start: pos, end: pos + span });
    }
    pos += span;
  }

  return ranges;
};

/** Clamp a cell's colSpan so it never extends past the table's last column. */
export const clampColSpan = (
  start: number,
  colSpan: number,
  totalColumns?: number,
): number =>
  totalColumns === undefined
    ? colSpan
    : Math.max(1, Math.min(colSpan, totalColumns - start));

/** A cell's colSpan minus the collapsed columns it covers, at least 1. */
export const getVisibleColSpan = (
  start: number,
  colSpan: number,
  ranges: ICollapsedColumnRange[],
): number => {
  const end = start + colSpan;
  const collapsed = ranges.reduce(
    (sum, range) =>
      sum +
      Math.max(0, Math.min(end, range.end) - Math.max(start, range.start)),
    0,
  );
  return Math.max(1, colSpan - collapsed);
};

export const getVisibleGroupColumnRanges = (
  headers: ICol[],
): ICollapsedColumnRange[] | null => {
  const isCollapsible = (header: ICol) =>
    header.type === "split" && Boolean(header.collapseColumn);

  const firstGroupIndex = headers.findIndex(isCollapsible);
  if (firstGroupIndex === -1) return null;
  if (!headers.some(h => isCollapsible(h) && h.collapsed)) return null;

  const ranges: ICollapsedColumnRange[] = [];
  let pos = 0;

  headers.forEach((header, index) => {
    const span = countLeafColumns([header]);
    const isPrefix = index < firstGroupIndex;
    const isCollapsed = header.type === "split" && header.collapsed;
    if (!isPrefix && !isCollapsed) {
      ranges.push({ start: pos, end: pos + span });
    }
    pos += span;
  });

  return ranges;
};

export type GroupRowKind = "real" | "info" | "empty";

interface RowWithCols {
  cols?: Record<string, ICol> | ICol[];
  subrows?: RowWithCols[];
}

export const getGroupRowKind = (
  row: RowWithCols,
  ranges: ICollapsedColumnRange[],
): GroupRowKind => {
  if (row.subrows) {
    const kinds = row.subrows.map(subrow => getGroupRowKind(subrow, ranges));
    if (kinds.includes("real")) return "real";
    return kinds.includes("info") ? "info" : "empty";
  }

  let kind: GroupRowKind = "empty";
  let columnOffset = 0;

  for (const cell of Object.values(row.cols ?? {})) {
    const colSpan = getColSpan(cell);
    const start = columnOffset;
    const end = columnOffset + colSpan;
    columnOffset = end;

    if (cell.hidden) continue;
    const overlaps = ranges.some(range => start < range.end && end > range.start);
    if (!overlaps) continue;

    if (cell.unit !== "BLANK") return "real";
    // Zero-width-space names are spacers, not labels.
    if (cell.name?.replace(/[\s\u200b]/g, "")) kind = "info";
  }

  return kind;
};

export const filterRowsForCollapsedGroups = <T extends RowWithCols>(
  rows: T[],
  headers: ICol[] | undefined,
): T[] => {
  const ranges = getVisibleGroupColumnRanges(headers ?? []);
  if (!ranges) return rows;

  const kinds = rows.map(row => getGroupRowKind(row, ranges));
  const lastRealIndex = kinds.lastIndexOf("real");

  return rows.filter(
    (_, index) =>
      kinds[index] === "real" ||
      (kinds[index] === "info" && index < lastRealIndex),
  );
};

/**
 * Undoes collapsed column groups for read-only views, where rows always
 * render every cell and a dropped group would leave headers out of line.
 *
 * @param headers - An array of header objects defining the table structure.
 * @returns The same headers with every group expanded as it was before.
 */
export const expandCollapsedHeaders = (headers: ICol[]): ICol[] => {
  if (!Array.isArray(headers)) return headers;
  const isTouched = (header: ICol) =>
    header.type === "split" &&
    (Boolean(header.collapsed) ||
      Boolean(
        header.cols?.some(col => (col as any)._collapsedHidden !== undefined),
      ));
  if (!headers.some(isTouched)) return headers;

  return headers.map(header => {
    if (!isTouched(header)) return header;
    return {
      ...header,
      collapsed: false,
      cols: header.cols?.map(col => {
        const {
          _collapsedHidden: hidden,
          _collapsedWidth: width,
          ...rest
        } = col as any;
        return {
          ...rest,
          ...(hidden !== undefined && { hidden }),
          ...(width !== undefined && { width: width ?? undefined }),
        } as ICol;
      }),
    };
  });
};
